import Box from '@mui/material/Box';
import { Chip, Divider, IconButton, LinearProgress, Link, Stack, Tooltip, Typography } from '@mui/material';
import {
  ArticleOutlined,
  CancelOutlined,
  CheckCircleOutlined,
  ErrorOutline,
  ImageOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import React from 'react';
import { PlainText } from '../../common/components/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import { ButtonUpload } from '../../common/components/Button';

/**
 *
 * @param label
 * @param href
 * @return {React.JSX.Element}
 * @constructor
 */
export const BlankRow = ({ label, href }) => (
  <>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1,
        minHeight: 20,
        transition: "background .15s",
        "&:hover": { bgcolor: "action.hover", textDecoration: "underline" },
      }}
    >
      <Link
        href={href}
        target="_blank"
        underline="none"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
          <ArticleOutlined
            sx={{ fontSize: 16, color: "text.link", flexShrink: 0 }}
          />
          <PlainText
            variant="body2"
            sx={{ color: "text.primary", fontWeight: 500, lineHeight: 1.35 }}
          >
            {label}
          </PlainText>
        </Stack>
      </Link>
    </Box>
  </>
);

/**
 *
 * @param label
 * @param required
 * @param field
 * @param fileName
 * @param fileLink
 * @param isLoading
 * @param onUpload
 * @param onDelete
 * @param isLast
 * @return {Element}
 * @constructor
 */
export const DocRow = ({
  label,
  required,
  field,
  fileName,
  fileLink,
  isLoading,
  onUpload,
  onDelete,
  isLast,
}) => {
  const uploaded = Boolean(fileName);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          minHeight: 40,
          transition: "background .15s",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", width: 16, height: 16 }}>
            {uploaded ? (
              <CheckCircleOutlined
                sx={{ fontSize: 16, color: "success.main" }}
              />
            ) : (
              required && <ErrorOutline sx={{ fontSize: 16, color: "error.light" }} />
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: uploaded ? 500 : 400,
              lineHeight: 1.35,
            }}
          >
            {label}
            {required && (
              <Typography
                component="span"
                sx={{ color: "error.main", ml: 0.25 }}
              >
                *
              </Typography>
            )}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          {uploaded ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  px: 1,
                  py: 0.4,
                  maxWidth: 200,
                }}
              >
                <Box sx={{ color: "primary.main", display: "flex" }}>
                  {getFileIcon(fileName)}
                </Box>
                <Tooltip title={fileName} placement="top">
                  <Link
                    href={fileLink || "#"}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{
                      fontSize: "0.72rem",
                      color: "text.secondary",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 150,
                    }}
                  >
                    {truncate(fileName)}
                  </Link>
                </Tooltip>
              </Box>
              <Tooltip title="Удалить файл">
                <IconButton
                  size="small"
                  onClick={() => onDelete(field)}
                  sx={{
                    color: "text.disabled",
                    "&:hover": { color: "error.main", bgcolor: "error.50" },
                  }}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <ButtonUpload
              onSubmit={(e) => onUpload(field, e)}
              title="Загрузить"
              disabled={isLoading}
              loading={isLoading}
            />
          )}
        </Stack>
      </Box>

      {/* loading bar */}
      {isLoading && (
        <LinearProgress sx={{ height: 2, mx: 2, borderRadius: 1, mb: 0.25 }} />
      )}

      {!isLast && <Divider sx={{ mx: 2 }} />}
    </>
  );
};

export const getFileIcon = (fileName) => {
  if (!fileName) return <InsertDriveFileOutlined fontSize="small" />;
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
    return <ImageOutlined fontSize="small" />;
  if (ext === 'pdf') return <PictureAsPdfOutlined fontSize="small" />;
  return <ArticleOutlined fontSize="small" />;
};

const truncate = (str, max = 32) =>
  str && str.length > max ? str.slice(0, max - 1) + "…" : str;

export const BoolChip = ({ value }) => (
  <Chip
    icon={
      value ? (
        <CheckCircleOutlined sx={{ fontSize: "13px !important" }} />
      ) : (
        <CancelOutlined sx={{ fontSize: "13px !important" }} />
      )
    }
    label={value ? "Да" : "Нет"}
    size="small"
    sx={{
      height: 22,
      fontWeight: 600,
      fontSize: "0.7rem",
      bgcolor: "transparent",
      color: value ? "success.main" : "text.disabled",
      border: "none",
      "& .MuiChip-icon": { color: value ? "success.main" : "text.disabled" },
    }}
  />
);

export const Row = ({ label, valueEl, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{ py: 0.9 }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 150 }}>
      {label}
    </Typography>
    {valueEl ?? (
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: "text.primary" }}
      >
        {value}
      </Typography>
    )}
  </Stack>
);